// Issue tracking shared by the control Worker (captureError) and the watchdog Worker (tail consumer).
// Pure: redaction, fingerprinting and the D1 upsert/alert rules. Tables in apps/control/migrations/0009_issues.sql.

import { sha256Hex, toHex } from './crypto.js'

export const ISSUE_STATUSES = ['open', 'resolved', 'ignored'] as const
export type IssueStatus = (typeof ISSUE_STATUSES)[number]
export type IssueSource = 'fetch' | 'cron' | 'alarm' | 'rpc' | 'browser' | 'tail'
export type IssueAlert = 'new' | 'regression' | 'spike'

export interface IssueRow {
  id: string
  fingerprint: string
  title: string
  type: string
  status: IssueStatus
  count: number
  first_seen: number
  last_seen: number
  release: string | null
  sample_stack: string | null
  last_alert_at: number | null
  source: IssueSource
}

export interface IssueEventRow {
  id: number
  issue_id: string
  at: number
  route: string | null
  client_id: string | null
  release: string | null
}

/** GET /api/operator/issues/:id */
export interface IssueDetail {
  issue: IssueRow
  events: IssueEventRow[]
}

const MIN = 60 * 1000
/**
 * Console marker of an error already recorded in the Worker (`captureError`). The watchdog's tail consumer skips the
 * exceptions of an invocation whose logs carry it, so a captured and rethrown error (DO alarm, RPC) is counted once.
 */
export const CAPTURED_MARKER = '[captured]'

/** True when one of the invocation's log lines carries CAPTURED_MARKER. */
export function logsHaveCapturedMarker(logs: ReadonlyArray<{ message: unknown }> | undefined): boolean {
  return !!logs?.some((l) => {
    const parts = Array.isArray(l.message) ? l.message : [l.message]
    return parts.some((p) => typeof p === 'string' && p.includes(CAPTURED_MARKER))
  })
}

export const ALERT_THROTTLE_MS = 60 * MIN
export const SPIKE_WINDOW_MS = 10 * MIN
export const SPIKE_THRESHOLD = 20
export const ISSUE_EVENT_RETENTION_MS = 30 * 24 * 60 * MIN

// ---------- redaction ----------

const REDACTIONS: [RegExp, string][] = [
  // Query strings carry tokens (access_token, code, appsecret_proof): drop them entirely.
  [/(https?:\/\/[^\s?#"'<>]+)\?[^\s"'<>]*/gi, '$1?[redacted]'],
  [/\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]+/gi, '$1 [redacted]'],
  [/\b(?:sk|pk|rk)[-_][A-Za-z0-9_-]{8,}/g, '[secret]'],
  [/\b(?:whsec|re|ghp|gho|ghs|github_pat|xox[bp])_[A-Za-z0-9_]{8,}/g, '[secret]'],
  // Meta access tokens.
  [/\bEA[A-Z][A-Za-z0-9]{20,}/g, '[secret]'],
  [/\b(access_token|refresh_token|token|secret|password|passwd|api[_-]?key|authorization|cookie|appsecret_proof|signature)(["']?\s*[:=]\s*["']?)[^\s"'&,;}]+/gi, '$1$2[redacted]'],
  // Message bodies inside JSON fragments.
  [/"(text|body|content|caption|message_body|preview)"\s*:\s*"(?:[^"\\]|\\.)*"/gi, '"$1":"[redacted]"'],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  // International numbers (+49 151 ..., 0049...) and bare 10-20 digit runs (WhatsApp IDs are phone numbers; PSIDs,
  // IGSIDs and Page IDs are 15-20 digits).
  [/(?:\+|\b00)\d[\d ().-]{6,}\d\b/g, '[phone]'],
  [/\b\d{10,20}\b/g, '[phone]'],
  // Long opaque strings (keys, signed values).
  [/\b[A-Za-z0-9_-]{40,}\b/g, '[secret]'],
]

/** Removes secrets, tokens, emails, phone numbers and message bodies. Safe to store and email. */
export function redact(s: string, max = 4000): string {
  let out = s
  for (const [re, to] of REDACTIONS) out = out.replace(re, to)
  return out.length > max ? `${out.slice(0, max)}…` : out
}

// ---------- fingerprint ----------

/** Message without the parts that vary per occurrence: URLs, UUIDs, hex, IDs and numbers. */
export function normaliseMessage(msg: string): string {
  return msg
    .replace(/\bhttps?:\/\/\S+/gi, '<url>')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<uuid>')
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>')
    .replace(/\b(?:0x)?[0-9a-f]{8,}\b/gi, '<hex>')
    // Mixed alphanumeric tokens with a digit (wamid.HBgM..., client ids, request ids).
    .replace(/\b(?=[A-Za-z0-9_.-]*\d)(?=[A-Za-z0-9_.-]*[A-Za-z])[A-Za-z0-9_.-]{12,}\b/g, '<id>')
    .replace(/\d+(?:\.\d+)?/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 500)
}

/** Top `n` stack frames as "function file" without line/column or build hashes, so they survive deploys. */
export function topFrames(stack: string | null | undefined, n = 3): string[] {
  if (!stack) return []
  const frames: string[] = []
  for (const raw of stack.split('\n')) {
    const line = raw.trim()
    // V8: "at fn (file:1:2)" / "at file:1:2"; Firefox/Safari: "fn@file:1:2".
    const m = /^at (?:(.+?) \()?(.+?)(?::\d+){0,2}\)?$/.exec(line) ?? /^([^\s@]*)@(\S+?)(?::\d+){0,2}$/.exec(line)
    if (!m) continue
    const fn = (m[1] ?? '').replace(/^async /, '').trim() || '<anonymous>'
    const file = (m[2] ?? '')
      .split(/[?#]/)[0]!
      .split('/')
      .pop()!
      .replace(/-[A-Za-z0-9_-]{6,}(?=\.m?js$)/, '')
    frames.push(`${fn} ${file}`)
    if (frames.length === n) break
  }
  return frames
}

export function fingerprint(type: string, message: string, stack?: string | null): Promise<string> {
  return sha256Hex([type, normaliseMessage(message), ...topFrames(stack)].join('\n'))
}

/** Path with ID-like segments replaced, no query: safe and groupable. */
export function normaliseRoute(pathOrUrl: string): string {
  let path = pathOrUrl
  try {
    path = new URL(pathOrUrl, 'https://x').pathname
  } catch {
    path = pathOrUrl.split(/[?#]/)[0]!
  }
  return path
    .split('/')
    .map((seg) => (/\d/.test(seg) && seg.length >= 6) || /^\d+$/.test(seg) || seg.includes('@') ? ':id' : seg)
    .join('/')
    .slice(0, 200)
}

// ---------- store ----------

/** The subset of D1Database used here (keeps this package free of Workers types). */
export interface IssuesStatement {
  bind(...values: unknown[]): IssuesStatement
  first<T = Record<string, unknown>>(): Promise<T | null>
  run(): Promise<{ meta: { changes: number } }>
  all<T = Record<string, unknown>>(): Promise<{ results: T[] }>
}
export interface IssuesDb {
  prepare(sql: string): IssuesStatement
}

export interface IssueInput {
  type: string
  message: string
  stack?: string | null
  source: IssueSource
  route?: string | null
  clientId?: string | null
  release?: string | null
  at: number
}

export interface RecordResult {
  issue: IssueRow
  /** Set when an email should go out now (the issue's last_alert_at has been claimed). */
  alert: IssueAlert | null
}

/**
 * Upserts the issue and appends an event. Alert rules: new issue, regression (resolved and seen again:
 * reopened), spike (> SPIKE_THRESHOLD events in SPIKE_WINDOW_MS). Ignored issues count but never alert.
 * At most one alert per issue per ALERT_THROTTLE_MS. Input must already be redacted.
 */
export async function recordIssue(db: IssuesDb, input: IssueInput): Promise<RecordResult> {
  const t = input.at
  const type = (input.type || 'Error').slice(0, 100)
  const fp = await fingerprint(type, input.message, input.stack)
  const title = `${type}: ${input.message}`.replace(/\s+/g, ' ').slice(0, 200)
  const prev = await db.prepare('SELECT status FROM issues WHERE fingerprint = ?').bind(fp).first<{ status: IssueStatus }>()
  const issue = await db
    .prepare(
      `INSERT INTO issues (id, fingerprint, title, type, status, count, first_seen, last_seen, release, sample_stack, last_alert_at, source)
       VALUES (?1, ?2, ?3, ?4, 'open', 1, ?5, ?5, ?6, ?7, NULL, ?8)
       ON CONFLICT (fingerprint) DO UPDATE SET
         count = count + 1,
         last_seen = MAX(last_seen, excluded.last_seen),
         release = COALESCE(excluded.release, release),
         sample_stack = COALESCE(excluded.sample_stack, sample_stack),
         status = CASE WHEN status = 'resolved' THEN 'open' ELSE status END
       RETURNING *`,
    )
    .bind(toHex(crypto.getRandomValues(new Uint8Array(10))), fp, title, type, t, input.release ?? null, input.stack ? input.stack.slice(0, 4000) : null, input.source)
    .first<IssueRow>()
  if (!issue) throw new Error('issue upsert returned no row')
  await db
    .prepare('INSERT INTO issue_events (issue_id, at, route, client_id, release) VALUES (?, ?, ?, ?, ?)')
    .bind(issue.id, t, input.route ?? null, input.clientId ?? null, input.release ?? null)
    .run()

  if (issue.status === 'ignored') return { issue, alert: null }
  if (issue.last_alert_at !== null && issue.last_alert_at > t - ALERT_THROTTLE_MS) return { issue, alert: null }

  let alert: IssueAlert | null = null
  if (!prev) alert = 'new'
  else if (prev.status === 'resolved') alert = 'regression'
  else {
    const recent = await db
      .prepare('SELECT COUNT(*) AS n FROM issue_events WHERE issue_id = ? AND at > ?')
      .bind(issue.id, t - SPIKE_WINDOW_MS)
      .first<{ n: number }>()
    if ((recent?.n ?? 0) > SPIKE_THRESHOLD) alert = 'spike'
  }
  if (!alert) return { issue, alert: null }
  // Claim the alert slot; a concurrent occurrence that already claimed it wins.
  const claimed = await db
    .prepare('UPDATE issues SET last_alert_at = ?1 WHERE id = ?2 AND (last_alert_at IS NULL OR last_alert_at <= ?3)')
    .bind(t, issue.id, t - ALERT_THROTTLE_MS)
    .run()
  if (claimed.meta.changes === 0) return { issue, alert: null }
  return { issue: { ...issue, last_alert_at: t }, alert }
}

/** Drops events older than 30 days, and resolved issues not seen for 30 days. */
export async function cleanIssueEvents(db: IssuesDb, t: number): Promise<void> {
  await db.prepare('DELETE FROM issue_events WHERE at < ?').bind(t - ISSUE_EVENT_RETENTION_MS).run()
  await db.prepare("DELETE FROM issues WHERE status = 'resolved' AND last_seen < ?").bind(t - ISSUE_EVENT_RETENTION_MS).run()
}

const ALERT_LABEL: Record<IssueAlert, string> = { new: 'New issue', regression: 'Regression', spike: 'Spike' }

/** Operator email for an issue alert. Everything in it is already redacted. */
export function issueAlertEmail(issue: IssueRow, alert: IssueAlert, ctx: { route?: string | null; appUrl?: string }): { subject: string; text: string } {
  const lines = [
    `${ALERT_LABEL[alert]}: ${issue.title}`,
    '',
    `source: ${issue.source}`,
    `route: ${ctx.route ?? '-'}`,
    `release: ${issue.release ?? '-'}`,
    `count: ${issue.count}`,
    `first seen: ${new Date(issue.first_seen).toISOString()}`,
    `last seen: ${new Date(issue.last_seen).toISOString()}`,
    '',
    ...(issue.sample_stack ? [issue.sample_stack.split('\n').slice(0, 8).join('\n'), ''] : []),
    ...(ctx.appUrl ? [`${ctx.appUrl.replace(/\/$/, '')}/app/operator#issue-${issue.id}`] : []),
  ]
  return { subject: `[lobby] ${ALERT_LABEL[alert]}: ${issue.title.slice(0, 120)}`, text: lines.join('\n') }
}
